"use client";

import React, { useEffect, useRef, useState } from "react";
import { Workflow, Cpu, Database, TrendingUp, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const modules = [{
  id: 1,
  title: "AI Strategy & Roadmap",
  description: "We apply our 40-year experience working with Fortune 500 companies to help SMBs navigate the complex AI landscape. Our methodology focuses on identifying high-ROI agentic opportunities, establishing clear governance, and aligning tech adoption with long-term business goals.\n\nQuoting & Estimating on Autopilot — handles job specs, calculates costs, outputs formatted quotes in minutes",
  iconName: "Workflow",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/ai_workflow_architecture.png"
}, {
  id: 2,
  title: "Process Optimization",
  description: "Before automation comes optimization. We streamline your core operations using lean methodologies, eliminating friction to ensure your business is perfectly primed for seamless AI agent integration and operational efficiency.\n\n\nBack-Office Work That Runs Itself — dispatch, scheduling, intake, follow-up — the stuff that used to need a person",
  iconName: "Cpu",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/automation_systems_engine.png"
}, {
  id: 3,
  title: "Agentic Solutions",
  description: "Deploying autonomous AI agents that handle complex, multi-step tasks. Our solutions act as a digital extension of your workforce, augmenting your team's capabilities and handling heavy operational lifting without increasing headcount.\n\n\nDone in Days, Not Months — real tools deployed in a focused sprint; no endless engagements",
  iconName: "Database",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/agentic_framework.png"
}, {
  id: 4,
  title: "Change Management",
  description: "Technology is only as effective as the people using it. We provide comprehensive change management frameworks that ensure your team is trained, aligned, and empowered to thrive in a modern AI-enhanced environment.",
  iconName: "TrendingUp",
  image: "https://learning-marmoset.10web.cloud/wp-content/uploads/2026/03/Gemini_Generated_Image_cb1foacb1foacb1f-scaled.png"
}, {
  id: 5,
  title: "Scalable Architecture",
  description: "Developing the internal systems and SOP frameworks required for exponential scaling. We build the foundational architecture that ensures your business remains agile, robust, and ready for rapid market expansion.",
  iconName: "Settings",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/operational_sop_network.png"
}];
const integrations = [{
  moduleId: 1,
  name: " Fortune 100 Tech Audit"
}, {
  moduleId: 1,
  name: " 3-Year Growth Plan"
}, {
  moduleId: 1,
  name: " Governance Framework"
}, {
  moduleId: 1,
  name: " Strategic Alignment"
}, {
  moduleId: 2,
  name: " Global Supply Chain"
}, {
  moduleId: 2,
  name: " 30% Efficiency Gain"
}, {
  moduleId: 2,
  name: " Lean Six Sigma"
}, {
  moduleId: 2,
  name: " Operational Audit"
}, {
  moduleId: 3,
  name: " FinServ Agent Suite"
}, {
  moduleId: 3,
  name: " 24/7 Ops Coverage"
}, {
  moduleId: 3,
  name: " Error Rate Reduction"
}, {
  moduleId: 3,
  name: " Custom LLM Logic"
}, {
  moduleId: 4,
  name: " Retail Workforce Shift"
}, {
  moduleId: 4,
  name: " 95% Adoption Rate"
}, {
  moduleId: 4,
  name: " Leadership Coaching"
}, {
  moduleId: 4,
  name: " Skills Upskilling"
}, {
  moduleId: 5,
  name: " Manufacturing Scale-up"
}, {
  moduleId: 5,
  name: " SOP Standardization"
}, {
  moduleId: 5,
  name: " Rapid Market Entry"
}, {
  moduleId: 5,
  name: " Risk Management"
}];
const iconMap: Record<string, React.ElementType> = {
  Workflow,
  Cpu,
  Database,
  TrendingUp,
  Settings
};
function ModuleRow({
  module,
  moduleIntegrations,
  isEven,
  index
}: {
  module: (typeof modules)[0];
  moduleIntegrations: typeof integrations;
  isEven: boolean;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const IconComponent = iconMap[module.iconName];
  return <div data-section-id="143854" data-index={index} className={`py-24 lg:py-32 ${isEven ? "bg-white" : "bg-slate-50"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`flex flex-col gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"}`}>
          {}
          <div className="w-full lg:w-1/2">
            <div className="relative aspect-[4/3] lg:aspect-square overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-xl shadow-slate-200/50">
              <div className={`${module.imageClassName || ""} absolute inset-0 bg-gradient-to-tr from-slate-900/5 to-transparent z-10 mix-blend-overlay`} />
              <img src={module.image} alt={module.title} className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            </div>
          </div>

          {}
          <div className="w-full lg:w-1/2 space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 shadow-sm">
                {IconComponent && <IconComponent className="w-8 h-8" />}
              </div>
              
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
                  {module.title}
                </h2>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                  {module.description}
                </p>
              </div>
            </div>

            
          </div>
        </div>
      </div>
    </div>;
}
export default function OurCapabilities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="our-specialized-solutions" className="bg-background text-foreground w-full overflow-hidden">
      {}
      <div className="container mx-auto px-4 md:px-6 pt-24 lg:pt-32 pb-12">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center space-y-6 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-bold tracking-widest text-blue-600 uppercase">
            Consulting Excellence
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">
            Our Specialized Solutions
          </h3>
          <p className="text-xl text-slate-600">Bringing decades of Global 500 transformation experience to the SMB sector through cutting-edge agentic frameworks.</p>
        </div>
      </div>

      {}
      <div className="flex flex-col w-full">
        {modules.map((module, index) => {
        const isEven = index % 2 === 0;
        const moduleIntegrations = integrations.filter(i => i.moduleId === module.id);
        return <ModuleRow key={module.id} module={module} moduleIntegrations={moduleIntegrations} isEven={isEven} index={index} />;
      })}
      </div>
    </section>;
}